import { useAuth } from '../../auth/AuthContext';

// Rebuilt around the current quota model: a one-time credit (not monthly)
// that only meters "expanded" simulations (>2 containers/items, palletized,
// or custom strategies) — saving/history and anything within 2 containers/
// 2 items is unconditional, so there's no "limit reached, blocked until
// next month" state to show anymore.
export function BillingTab({ historyApi }) {
  const { sims } = historyApi;
  const { user } = useAuth();
  const isPaid = user?.plan === 'paid';
  const used = user?.freeSimCreditsUsed ?? 0;
  const limit = user?.freeSimCreditsLimit ?? 10;
  const pct = isPaid ? 0 : Math.min((used / limit) * 100, 100);
  const meterColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-400' : 'bg-primary';

  const sorted = [...sims].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Console</p>
        <h1 className="mt-1 text-3xl font-black text-white">Billing</h1>
      </div>

      {/* Expanded-simulation credit card */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 mb-5">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Expanded Simulation Credits</p>
        <p className="mt-1 text-xs text-slate-500">
          Only counts simulations beyond 2 containers/2 items, palletized, or custom strategies — everything else is always free, and saving/history is never capped.
        </p>

        {isPaid ? (
          <div className="mt-4 flex items-end gap-3">
            <span className="text-5xl font-black text-white">Unlimited</span>
          </div>
        ) : (
          <>
            <div className="mt-4 flex items-end gap-3">
              <span className="text-5xl font-black text-white">{used}</span>
              <span className="text-xl text-slate-400 pb-1">/ {limit}</span>
              <span className="text-sm text-slate-500 pb-1">used</span>
            </div>

            <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className={`h-full rounded-full transition-all duration-700 ${meterColor}`}
                style={{ width: `${pct}%` }}
              />
            </div>

            {pct >= 100 && (
              <p className="mt-2.5 text-xs font-semibold text-red-400">
                All free expanded simulations used — upgrade to keep running these. Simulations within 2 containers/2 items still work.
              </p>
            )}
            {pct >= 70 && pct < 100 && (
              <p className="mt-2.5 text-xs text-yellow-400">
                {limit - used} expanded simulation{limit - used !== 1 ? 's' : ''} remaining.
              </p>
            )}
          </>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">All-Time Total</p>
          <p className="mt-2 text-4xl font-black text-white">{sims.length}</p>
          <p className="text-xs text-slate-500 mt-0.5">simulations saved</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Current Plan</p>
          <p className="mt-2 text-4xl font-black text-white">{isPaid ? 'Paid' : 'Free'}</p>
          <p className="text-xs text-slate-500 mt-0.5">{isPaid ? 'unlimited simulations' : `${limit} expanded sims, one-time`}</p>
        </div>
      </div>

      {/* Simulation log */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Simulation Log</p>

        {sorted.length === 0 ? (
          <p className="text-sm text-slate-500">No simulations yet.</p>
        ) : (
          <div className="divide-y divide-white/[0.06]">
            {sorted.map((sim) => (
              <div key={sim.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{sim.name}</p>
                  {sim.parentId && sims.find(s => (s._id || s.id) === sim.parentId)?.name && (
                    <p className="text-xs text-slate-500">↳ forked from {sims.find(s => (s._id || s.id) === sim.parentId).name}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold
                    ${sim.handlingType === 'palletized'
                      ? 'bg-violet-500/20 text-violet-300'
                      : 'bg-cyan-500/20 text-cyan-300'}`}>
                    {sim.handlingType === 'palletized' ? 'Palletized' : 'Hand Stuffed'}
                  </span>
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {new Date(sim.createdAt).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
